import React, { Suspense, lazy } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "../context/AuthContext";
import { LabourProvider } from "../context/Admin/LabourContext";
import AssetProvider from "../context/Admin/AssetContext";
import SecurityDepositsProvider from "../context/Admin/SecurityDepositsContext";
import EMDProvider from "../context/Admin/EMDContext";
import AccountsProvider from "../context/Admin/AccountsContext";
import ExpenseBillsProvider from "../context/Admin/ExpenseBillsContext";
import TaxTypesProvider from "../context/Admin/TaxTypesContext";
import WorkLocationsProvider from "../context/Admin/WorkLocationsContext";
import HolidaysProvider from "../context/Admin/HolidaysContext";
import ProjectSectionsProvider from "../context/Admin/ProjectSectionsContext";
import WorkCategoriesProvider from "../context/Admin/WorkCategoriesContext";
import WorkTypesProvider from "../context/Admin/WorkTypesContext";
import BankDetailsProvider from "../context/Admin/BankDetailsContext";
import MaterialProvider from "../context/Admin/MaterialContext";
import SuppliersProvider from "../context/Admin/SuppliersContext";
import ServicesProvider from "../context/Admin/ServicesContext";
import ServiceTypesProvider from "../context/Admin/ServiceTypesContext";
import SalesCustomersProvider from "../context/Admin/SalesCustomersContext";
import { MastersProvider } from "../context/Admin/MastersContext";
import ClientsProvider from "../context/Admin/ClientsContext";
import DailyNotesProvider from "../context/Admin/DailyNotesContext";
import DailyTasksProvider from "../context/Admin/DailyTasksContext";
import RemindersProvider from "../context/Admin/RemindersContext";
import VehicleProvider from "../context/Admin/VehicleContext";
import ToolProvider from "../context/Admin/ToolContext";
import TravelAllowanceProvider from "../context/Admin/TravelAllowanceContext";
import { EmployeeProvider } from "../context/Admin/EmployeeContext";
import { ProjectProvider } from "../context/Admin/ProjectContext";
import { UserProvider } from "../context/Admin/UserContext";
import RolesProvider from "../context/Admin/RolesContext";
import PermissionsProvider from "../context/Admin/PermissionsContext";
import ActivityLogProvider from "../context/Admin/ActivityLogContext";
import InvoiceProvider from "../context/Admin/InvoiceContext";
import ClientPaymentProvider from "../context/Admin/ClientPaymentContext";
import { QuotationsProvider } from "../context/Admin/QuotationsContext";
import SalarySlipProvider from "../context/Admin/SalarySlipContext";
import WageSlipProvider from "../context/Admin/WageSlipContext";
import DailyLabourLogProvider from "../context/Admin/DailyLabourLogContext";

const Dashboard = lazy(() => import("../pages/Admin/Dashboard"));
const Settings = lazy(() => import("../pages/Admin/Settings"));
const UserManagement = lazy(() => import("../pages/Admin/UserManagement"));
const Enquiries = lazy(() => import("../pages/Admin/Enquiries"));
const Quotations = lazy(() => import("../pages/Admin/Quotations"));
const Projects = lazy(() => import("../pages/Admin/Projects"));
const ProjectDetails = lazy(() => import("../pages/Admin/ProjectDetails"));
const SubcontractWork = lazy(() => import("../pages/Admin/SubcontractWork"));
const SubContractDetails = lazy(() => import("../pages/Admin/SubContractDetails"));
const Assets = lazy(() => import("../pages/Admin/Assets"));
const SecurityDeposit = lazy(() => import("../pages/Admin/SecurityDeposit"));
const EMD = lazy(() => import("../pages/Admin/EMD"));
const Accounts = lazy(() => import("../pages/Admin/Accounts"));
const Purchases = lazy(() => import("../pages/Admin/Purchases"));
const StockManagements = lazy(() => import("../pages/Admin/StockManagements"));
const Reports = lazy(() => import("../pages/Admin/Reports"));
const AttendanceReport = lazy(() => import("../pages/Admin/AttendanceReport"));
const ActivityLogs = lazy(() => import("../pages/Admin/ActivityLogs"));
const Expenses = lazy(() => import("../pages/Admin/Expenses"));
const LabourManagement = lazy(() => import("../pages/Admin/LabourManagement"));
const VehicleManagement = lazy(() => import("../pages/Admin/VehicleManagement"));
const HR = lazy(() => import("../pages/Admin/HR"));
const Employees = lazy(() => import("../pages/Admin/Employees"));
const ToolsManagement = lazy(() => import("../pages/Admin/ToolsManagement"));
const TravelAllowance = lazy(() => import("../pages/Admin/TravelAllowance"));
const Reminders = lazy(() => import("../pages/Admin/Reminders"));
const ProjectDocuments = lazy(() => import("../pages/Admin/ProjectDocuments"));
const DailyTasks = lazy(() => import("../pages/Admin/DailyTasks"));
const DailyNotes = lazy(() => import("../pages/Admin/DailyNotes"));
const Clients = lazy(() => import("../pages/Admin/Clients"));
const SalesCustomers = lazy(() => import("../pages/Admin/SalesCustomers"));
const Services = lazy(() => import("../pages/Admin/Services"));
const Suppliers = lazy(() => import("../pages/Admin/Suppliers"));
const Materials = lazy(() => import("../pages/Admin/Materials"));
const BankDetails = lazy(() => import("../pages/Admin/BankDetails"));
const WorkTypes = lazy(() => import("../pages/Admin/WorkTypes"));
const WorkCategories = lazy(() => import("../pages/Admin/WorkCategories"));
const ProjectSections = lazy(() => import("../pages/Admin/ProjectSections"));
const Holidays = lazy(() => import("../pages/Admin/Holidays"));
const WorkLocations = lazy(() => import("../pages/Admin/WorkLocations"));
const TaxTypes = lazy(() => import("../pages/Admin/TaxTypes"));
const SalarySlips = lazy(() => import("../pages/Admin/SalarySlips"));
const WageSlips = lazy(() => import("../pages/Admin/WageSlips"));
const DailyLabourLogs = lazy(() => import("../pages/Admin/DailyLabourLogs"));
const SubscriptionBilling = lazy(() => import("../pages/Admin/SubscriptionBilling").then(m => ({ default: m.SubscriptionBilling })));
const SuperAdminDashboard = lazy(() => import("../pages/SuperAdmin/SuperAdminDashboard"));
const TenantManagement = lazy(() => import("../pages/SuperAdmin/TenantManagement"));
const PlanManager = lazy(() => import("../pages/SuperAdmin/PlanManager"));

const RouteFallback: React.FC = () => (
    <div className="flex flex-col gap-3 p-6">
        <div className="cms-skeleton h-8 w-1/3" />
        <div className="cms-skeleton h-4 w-full" />
        <div className="cms-skeleton h-4 w-5/6" />
        <div className="cms-skeleton h-64 w-full" />
    </div>
);

const LazyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Suspense fallback={<RouteFallback />}>{children}</Suspense>
);

const AdminRoutes: React.FC = () => {
    const { roles } = useAuth();
    const isSuperAdmin = (roles.includes("Super_Admin") || roles.includes("Super Admin") || roles.includes("super_admin")) && sessionStorage.getItem("is_impersonating") !== "true";

    return (
        <Routes>
            {/* Dashboard */}
            <Route
                path="/dashboard"
                element={
                    isSuperAdmin ? (
                        <Navigate to="/super-admin" replace />
                    ) : (
                        <ProtectedRoute action="view_dashboard">
                            <LazyRoute>
                                <Dashboard />
                            </LazyRoute>
                        </ProtectedRoute>
                    )
                }
            />

            {/* Settings */}
            <Route
                path="/settings"
                element={
                    <ProtectedRoute action="configure_settings">
                        <UserProvider>
                            <RolesProvider>
                                <PermissionsProvider>
                                    <LazyRoute>
                                        <Settings />
                                    </LazyRoute>
                                </PermissionsProvider>
                            </RolesProvider>
                        </UserProvider>
                    </ProtectedRoute>
                }
            />

            {/* User Management */}
            <Route
                path="/users"
                element={
                    <ProtectedRoute action="manage_users">
                      <UserProvider>
                          <RolesProvider>
                              <LazyRoute>
                                  <UserManagement />
                              </LazyRoute>
                          </RolesProvider>
                      </UserProvider>
                    </ProtectedRoute>
                }
            />

            {/* Enquiries and Quotations */}
            <Route
                path="/enquiries"
                element={
                    <ProtectedRoute action="view_enquiries">
                        <LazyRoute>
                            <Enquiries />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/quotations"
                element={
                    <ProtectedRoute action="view_quotations">
                        <SalesCustomersProvider>
                            <QuotationsProvider>
                                <LazyRoute>
                                    <Quotations />
                                </LazyRoute>
                            </QuotationsProvider>
                        </SalesCustomersProvider>
                    </ProtectedRoute>
                }
            />

            {/* Projects and Subcontract Works */}
            <Route
                path="/projects"
                element={
                    <ProtectedRoute action="view_projects">
                        <ProjectProvider>
                            <MastersProvider>
                                <LazyRoute>
                                    <Projects />
                                </LazyRoute>
                            </MastersProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/projects/:id"
                element={
                    <ProtectedRoute action="view_project_details">
                        <ProjectProvider>
                            <MastersProvider>
                                <LazyRoute>
                                    <ProjectDetails />
                                </LazyRoute>
                            </MastersProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/subcontract-works"
                element={
                    <ProtectedRoute action="manage_subcontract_works">
                        <LazyRoute>
                            <SubcontractWork />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/subcontract-works/:subcontractId"
                element={
                    <ProtectedRoute action="manage_subcontract_works">
                        <LazyRoute>
                            <SubContractDetails />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />

            {/* Assets */}
            <Route
                path="/assets"
                element={
                    <ProtectedRoute action="manage_assets">
                        <AssetProvider>
                            <LazyRoute>
                                <Assets />
                            </LazyRoute>
                        </AssetProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/security-deposits"
                element={
                    <ProtectedRoute action="manage_security_deposits">
                        <ProjectProvider>
                            <ClientsProvider>
                                <SuppliersProvider>
                                    <SecurityDepositsProvider>
                                        <LazyRoute>
                                            <SecurityDeposit />
                                        </LazyRoute>
                                    </SecurityDepositsProvider>
                                </SuppliersProvider>
                            </ClientsProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/EMD"
                element={
                    <ProtectedRoute action="manage_EMD">
                        <ProjectProvider>
                            <ClientsProvider>
                                <EMDProvider>
                                    <LazyRoute>
                                        <EMD projectName={undefined} />
                                    </LazyRoute>
                                </EMDProvider>
                            </ClientsProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />

            {/* Accounts */}
            <Route
                path="/accounts"
                element={
                    <ProtectedRoute action="manage_accounts">
                        <AccountsProvider>
                            <LazyRoute>
                                <Accounts />
                            </LazyRoute>
                        </AccountsProvider>
                    </ProtectedRoute>
                }
            />

            {/* Purchases and Stock Management */}
            <Route
                path="/purchases"
                element={
                    <ProtectedRoute action="view_purchase_requests">
                        <ProjectProvider>
                            <SuppliersProvider>
                                <MaterialProvider>
                                    <UserProvider>
                                        <LazyRoute>
                                            <Purchases />
                                        </LazyRoute>
                                    </UserProvider>
                                </MaterialProvider>
                            </SuppliersProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/stock-management"
                element={
                    <ProtectedRoute action="manage_stock">
                        <ProjectProvider>
                            <MaterialProvider>
                                <LazyRoute>
                                    <StockManagements />
                                </LazyRoute>
                            </MaterialProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />

            {/* Reports */}
            <Route
                path="/reports"
                element={
                    <ProtectedRoute action="view_reports">
                        <ProjectProvider>
                            <InvoiceProvider>
                                <ClientPaymentProvider>
                                    <ExpenseBillsProvider>
                                        <LazyRoute>
                                            <Reports />
                                        </LazyRoute>
                                    </ExpenseBillsProvider>
                                </ClientPaymentProvider>
                            </InvoiceProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/attendance-report"
                element={
                    <ProtectedRoute action="view_attendance_reports">
                        <LazyRoute>
                            <AttendanceReport />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/activity-logs"
                element={
                    <ProtectedRoute action="view_activity_logs">
                        <ActivityLogProvider>
                            <LazyRoute>
                                <ActivityLogs />
                            </LazyRoute>
                        </ActivityLogProvider>
                    </ProtectedRoute>
                }
            />

            {/* Expenses and Labour Management */}
            <Route
                path="/expenses"
                element={
                    <ProtectedRoute action="log_expenses">
                        <ExpenseBillsProvider>
                            <LazyRoute>
                                <Expenses />
                            </LazyRoute>
                        </ExpenseBillsProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/labour-management"
                element={
                    <ProtectedRoute action="manage_labour">
                        <LabourProvider>
                            <LazyRoute>
                                <LabourManagement />
                            </LazyRoute>
                        </LabourProvider>
                    </ProtectedRoute>
                }
            />

            {/* Vehicle Management, HR, Tools Management */}
            <Route
                path="/vehicle-management"
                element={
                    <ProtectedRoute action="manage_vehicle">
                        <VehicleProvider>
                            <LazyRoute>
                                <VehicleManagement />
                            </LazyRoute>
                        </VehicleProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/hr"
                element={
                    <ProtectedRoute action="view_hr">
                        <LazyRoute>
                            <HR />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/salary-slips"
                element={
                    <ProtectedRoute action="view_hr">
                        <EmployeeProvider>
                            <SalarySlipProvider>
                                <LazyRoute>
                                    <SalarySlips />
                                </LazyRoute>
                            </SalarySlipProvider>
                        </EmployeeProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/wage-slips"
                element={
                    <ProtectedRoute action="view_hr">
                        <LabourProvider>
                            <ProjectProvider>
                                <WageSlipProvider>
                                    <LazyRoute>
                                        <WageSlips />
                                    </LazyRoute>
                                </WageSlipProvider>
                            </ProjectProvider>
                        </LabourProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/daily-labour-logs"
                element={
                    <ProtectedRoute action="view_hr">
                        <LabourProvider>
                            <ProjectProvider>
                                <DailyLabourLogProvider>
                                    <LazyRoute>
                                        <DailyLabourLogs />
                                    </LazyRoute>
                                </DailyLabourLogProvider>
                            </ProjectProvider>
                        </LabourProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/employees"
                element={
                    <ProtectedRoute action="manage_employees">
                        <EmployeeProvider>
                            <LazyRoute>
                                <Employees />
                            </LazyRoute>
                        </EmployeeProvider>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/tools-management"
                element={
                    <ProtectedRoute action="manage_tools">
                        <ToolProvider>
                            <LazyRoute>
                                <ToolsManagement />
                            </LazyRoute>
                        </ToolProvider>
                    </ProtectedRoute>
                }
            />

            {/* Travel Allowance */}
            <Route
                path="/travel-allowance"
                element={
                    <ProtectedRoute action="approve_travel_allowance">
                        <EmployeeProvider>
                            <ProjectProvider>
                                <TravelAllowanceProvider>
                                    <LazyRoute>
                                        <TravelAllowance />
                                    </LazyRoute>
                                </TravelAllowanceProvider>
                            </ProjectProvider>
                        </EmployeeProvider>
                    </ProtectedRoute>
                }
            />

            {/* Other Modules */}
            <Route
                path="/reminders"
                element={
                    <ProtectedRoute action="set_reminders">
                        <ProjectProvider>
                            <EmployeeProvider>
                                <RemindersProvider>
                                    <LazyRoute>
                                        <Reminders />
                                    </LazyRoute>
                                </RemindersProvider>
                            </EmployeeProvider>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/project-documents"
                element={
                    <ProtectedRoute action="manage_project_documents">
                        <ProjectProvider>
                            <LazyRoute>
                                <ProjectDocuments />
                            </LazyRoute>
                        </ProjectProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/daily-tasks"
                element={
                    <ProtectedRoute action="track_daily_tasks">
                        <DailyTasksProvider>
                            <LazyRoute>
                                <DailyTasks />
                            </LazyRoute>
                        </DailyTasksProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/daily-notes"
                element={
                    <ProtectedRoute action="write_daily_notes">
                        <DailyNotesProvider>
                            <LazyRoute>
                                <DailyNotes />
                            </LazyRoute>
                        </DailyNotesProvider>
                    </ProtectedRoute>
                }
            />

            {/* Data Management */}
            <Route
                path="/clients"
                element={
                    <ProtectedRoute action="manage_clients">
                        <ClientsProvider>
                            <LazyRoute>
                                <Clients />
                            </LazyRoute>
                        </ClientsProvider>
                    </ProtectedRoute>
                }
            />
           
            <Route
                path="/sales-customers"
                element={
                    <ProtectedRoute action="manage_sales_customers">
                        <SalesCustomersProvider>
                            <LazyRoute>
                                <SalesCustomers />
                            </LazyRoute>
                        </SalesCustomersProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/services"
                element={
                    <ProtectedRoute action="define_services">
                        <ServicesProvider>
                            <ServiceTypesProvider>
                                <LazyRoute>
                                    <Services />
                                </LazyRoute>
                            </ServiceTypesProvider>
                        </ServicesProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/suppliers"
                element={
                    <ProtectedRoute action="manage_suppliers">
                        <SuppliersProvider>
                            <LazyRoute>
                                <Suppliers />
                            </LazyRoute>
                        </SuppliersProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/materials"
                element={
                    <ProtectedRoute action="manage_materials">
                        <WorkCategoriesProvider>
                            <ProjectSectionsProvider>
                                <MaterialProvider>
                                    <LazyRoute>
                                        <Materials />
                                    </LazyRoute>
                                </MaterialProvider>
                            </ProjectSectionsProvider>
                        </WorkCategoriesProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/bank-details"
                element={
                    <ProtectedRoute action="manage_bank_accounts">
                        <BankDetailsProvider>
                            <LazyRoute>
                                <BankDetails />
                            </LazyRoute>
                        </BankDetailsProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/work-types"
                element={
                    <ProtectedRoute action="define_work_types">
                        <WorkCategoriesProvider>
                            <WorkTypesProvider>
                                <LazyRoute>
                                    <WorkTypes />
                                </LazyRoute>
                            </WorkTypesProvider>
                        </WorkCategoriesProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/work-categories"
                element={
                    <ProtectedRoute action="define_work_categories">
                        <WorkCategoriesProvider>
                            <LazyRoute>
                                <WorkCategories />
                            </LazyRoute>
                        </WorkCategoriesProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/project-sections"
                element={
                    <ProtectedRoute action="define_project_sections">
                        <WorkLocationsProvider>
                            <ProjectSectionsProvider>
                                <LazyRoute>
                                    <ProjectSections />
                                </LazyRoute>
                            </ProjectSectionsProvider>
                        </WorkLocationsProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/holidays"
                element={
                    <ProtectedRoute action="define_holidays">
                        <HolidaysProvider>
                            <LazyRoute>
                                <Holidays />
                            </LazyRoute>
                        </HolidaysProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/work-locations"
                element={
                    <ProtectedRoute action="define_work_locations">
                        <WorkLocationsProvider>
                            <LazyRoute>
                                <WorkLocations />
                            </LazyRoute>
                        </WorkLocationsProvider>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/tax-types"
                element={
                    <ProtectedRoute action="define_tax_types">
                        <TaxTypesProvider>
                            <LazyRoute>
                                <TaxTypes />
                            </LazyRoute>
                        </TaxTypesProvider>
                    </ProtectedRoute>
                }
            />
            {/* SaaS Subscription Billing Route */}
            <Route
                path="/subscription"
                element={
                    <LazyRoute>
                        <SubscriptionBilling />
                    </LazyRoute>
                }
            />
            {/* Super Admin Platform Governance Routes (under /super-admin/* and /admin/super-admin/*) */}
            <Route
                path="/"
                element={
                    isSuperAdmin ? (
                        <ProtectedRoute action="manage_platform">
                            <LazyRoute>
                                <SuperAdminDashboard />
                            </LazyRoute>
                        </ProtectedRoute>
                    ) : (
                        <Navigate to="/admin/dashboard" replace />
                    )
                }
            />
            <Route
                path="/super-admin"
                element={<Navigate to="/super-admin" replace />}
            />
            <Route
                path="/super-admin/tenants"
                element={<Navigate to="/super-admin/tenants" replace />}
            />
            <Route
                path="/super-admin/plans"
                element={<Navigate to="/super-admin/plans" replace />}
            />
            <Route
                path="/tenants"
                element={
                    <ProtectedRoute action="manage_all_companies">
                        <LazyRoute>
                            <TenantManagement />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/plans"
                element={
                    <ProtectedRoute action="manage_plans">
                        <LazyRoute>
                            <PlanManager />
                        </LazyRoute>
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
};

export default AdminRoutes;
